/**
 * Cancel Ritual Dialog
 * Confirmation dialog for canceling a ritual
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { X } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"
import { apiClient } from "@/lib/api-client"

interface CancelRitualDialogProps {
  isOpen: boolean
  onClose: () => void
  ritualId: string
  ritualTitle: string
}

export function CancelRitualDialog({
  isOpen,
  onClose,
  ritualId,
  ritualTitle,
}: CancelRitualDialogProps) {
  const { t } = useTranslation("rituals")
  const queryClient = useQueryClient()

  const cancelRitualMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`/api/rituals/${ritualId}/cancel`)
      return response.data
    },
    onSuccess: () => {
      toast.success(t("cancelRitual.success"))
      // Invalidate the ritual query to refresh the data
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["rituals"] })
      onClose()
      // Navigate to the ritual detail page
      window.location.href = `/rituals/${ritualId}`
    },
    onError: (error: { response?: { data?: { message?: string } }; message?: string }) => {
      const message = error.response?.data?.message || error.message || t("cancelRitual.error")
      toast.error(message)
    },
  })

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        overlayClass="bg-[#020202E5]"
        className="md:w-[520px] bg-[#020202] border border-[#F2F0EB4D] text-[#F2F0EB] pt-12.5 pb-10 px-12.5 gap-7.5"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 text-[#F2F0EB] bg-[#F2F0EB0D] border border-[#F2F0EB26] p-2.5 rounded-full transition-opacity hover:opacity-100 focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader className="text-center gap-2.5">
          <DialogTitle className="text-[#F2F0EBE6] text-xl font-semibold text-center leading-6">
            {t("cancelRitual.title")}
          </DialogTitle>
          <DialogDescription className="text-[#F2F0EBE6] text-center text-sm leading-4.25">
            {t("cancelRitual.description", { ritualTitle })}
          </DialogDescription>
        </DialogHeader>

        <div className="text-[#F2F0EB99] text-sm leading-4.25 text-center">
          {t("cancelRitual.warning")}
        </div>

        <DialogFooter className="flex-row gap-2.5 sm:flex-row">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={cancelRitualMutation.isPending}
            className="flex-1 h-[50px] md:w-[205px] py-3.75 uppercase"
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="primary-terracotta"
            onClick={() => cancelRitualMutation.mutate()}
            disabled={cancelRitualMutation.isPending}
            className="flex-1 h-[50px] md:w-[205px] py-3.75 uppercase"
          >
            {cancelRitualMutation.isPending
              ? t("cancelRitual.canceling")
              : t("cancelRitual.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
